import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Sparkles, ArrowUp, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";

const SUGGESTIONS = [
  "I run a local med-spa and want a full content calendar - where do I start?",
  "We're a boutique agency with 15 clients. How does the Studio plug in?",
  "I'm pre-launch with a new product idea - how fast can we go live?",
];

const SEED = {
  id: "seed",
  role: "assistant",
  content:
    "I'm the GoldKey Studio Advisor. Tell me about your business in one line - what you do, who it's for, and the number you most want to move - and I'll sketch a clear three-step roadmap you can start the day you pick a plan.",
};

const FALLBACK_MESSAGE =
  "Hmm - the advisor is napping. Hit the demo button up top to book time with a human, and I'll be back shortly.";

// ------- Subcomponents -------

function SuggestionsList({ suggestions, sending, onPick }) {
  return (
    <div className="mt-8 sm:mt-10 flex flex-col gap-3">
      <p className="overline">Try a prompt</p>
      {suggestions.map((s, i) => (
        <button
          key={s}
          type="button"
          onClick={() => onPick(s)}
          disabled={sending}
          data-testid={`ai-suggestion-${i}`}
          className="text-left text-sm text-neutral-700 border border-neutral-200 px-4 py-3 hover:border-gold/50 hover:text-neutral-900 transition disabled:opacity-50"
        >
          {s}
        </button>
      ))}
    </div>
  );
}

function MessageList({ messages, sending, scrollRef }) {
  return (
    <div
      ref={scrollRef}
      className="h-[420px] sm:h-[460px] overflow-y-auto px-5 sm:px-7 py-6 space-y-5"
    >
      {messages.map((m) => (
        <div
          key={m.id}
          data-testid={`ai-msg-${m.role}-${m.id}`}
          className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
        >
          <div
            className={`max-w-[88%] px-4 py-3 text-sm sm:text-[15px] leading-relaxed whitespace-pre-wrap ${
              m.role === "user"
                ? "bg-gold text-[#1a1206]"
                : "bg-[#FAFAFA] border border-neutral-200 text-neutral-800"
            }`}
          >
            {m.content}
          </div>
        </div>
      ))}
      {sending && (
        <div
          className="flex items-center gap-2 text-neutral-500 text-sm"
          data-testid="ai-thinking"
        >
          <Loader2 size={14} className="animate-spin text-gold" />
          Thinking…
        </div>
      )}
    </div>
  );
}

function ChatInput({ value, onChange, onSubmit, disabled }) {
  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="flex items-end gap-3 border-t border-gold/15 px-5 sm:px-7 py-4"
    >
      <textarea
        rows={1}
        data-testid="ai-chat-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKey}
        placeholder="Tell the advisor about your business…"
        className="editorial-input flex-1 resize-none !py-2"
        disabled={disabled}
      />
      <button
        type="submit"
        disabled={disabled || !value.trim()}
        data-testid="ai-chat-send"
        aria-label="Send message"
        className="w-11 h-11 inline-flex items-center justify-center bg-gold text-[#1a1206] hover:bg-gold-soft transition disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ArrowUp size={16} strokeWidth={2.2} />
      </button>
    </form>
  );
}

// ------- Main component -------

let messageCounter = 0;
const nextMessageId = () => `msg-${++messageCounter}`;

export default function AIChatDemo() {
  const [messages, setMessages] = useState([SEED]);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);
  const reactId = useId();

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = useCallback(
    async (text) => {
      const value = (text ?? input).trim();
      if (!value || sending) return;
      setError(null);
      setMessages((m) => [...m, { id: nextMessageId(), role: "user", content: value }]);
      setInput("");
      setSending(true);
      track("Studio Advisor Message", { length: value.length });
      try {
        const { data } = await api.post("/ai/chat", { session_id: sessionId, message: value });
        if (data.session_id && !sessionId) setSessionId(data.session_id);
        setMessages((m) => [...m, { id: nextMessageId(), role: "assistant", content: data.reply }]);
      } catch (e) {
        const detail = e?.response?.data?.detail || "Something went sideways. Try again?";
        setError(detail);
        setMessages((m) => [
          ...m,
          { id: nextMessageId(), role: "assistant", content: FALLBACK_MESSAGE },
        ]);
      } finally {
        setSending(false);
      }
    },
    [input, sending, sessionId],
  );

  return (
    <section
      data-testid="ai-chat-demo"
      aria-labelledby={`${reactId}-title`}
      className="relative py-20 sm:py-28 lg:py-36 px-5 sm:px-6 lg:px-12"
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        <div className="lg:col-span-5">
          <p className="overline">Try the studio advisor</p>
          <h2
            id={`${reactId}-title`}
            className="font-serif text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.05] mt-5 sm:mt-6"
          >
            Get a clear
            <br />
            <span className="italic text-aurora">first step.</span>
          </h2>
          <p className="text-neutral-700 leading-relaxed text-base sm:text-lg mt-6 sm:mt-8 max-w-md">
            A live preview of how the Studio thinks. Tell the advisor about
            your business and you&apos;ll get a clean three-step plan - what to
            build first, how to launch it, and how we&apos;ll track results.
          </p>
          <SuggestionsList suggestions={SUGGESTIONS} sending={sending} onPick={send} />
        </div>

        <div className="lg:col-span-7">
          <div
            data-testid="ai-chat-card"
            className="filigree-border bg-white/70 backdrop-blur-xl"
          >
            <div className="flex items-center gap-3 px-5 sm:px-7 py-4 border-b border-gold/15">
              <span className="w-2 h-2 rounded-full bg-gold animate-pulse" />
              <span className="text-xs uppercase tracking-[0.22em] text-neutral-700">
                GoldKey Studio Advisor · Live
              </span>
              <Sparkles size={14} className="ml-auto text-gold" />
            </div>

            <MessageList messages={messages} sending={sending} scrollRef={scrollRef} />

            <ChatInput
              value={input}
              onChange={setInput}
              onSubmit={() => send()}
              disabled={sending}
            />

            {error && (
              <p className="px-5 sm:px-7 pb-4 text-xs text-rose-300" data-testid="ai-chat-error">
                {error}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
